// LIBRERIE/IMPORTS
import React from "react";
import { useTranslation } from "react-i18next";

// CONFIGURAZIONI E UTILS
import propertyFieldsConfig from "../../config/propertyFields.json";
import {
    getContractTranslation,
    getPropertyStatusTranslation,
    getHeatingTypeTranslation,
    getHeatingFuelNameTranslation,
    getFurnitureTranslation,
    getFloorTranslation,
    LanguageCode
} from '../../config/i18n/elementsMappingProperty';

interface PropertyDetailsProps {
    propertyDetails: any;
    isResidential: boolean;
    isPlot: boolean;
    isGarage: boolean;
    isValidValue: (value: any) => boolean;
    getNestedValue: (obj: any, path: string) => any;
    decodeHTMLEntities: (text: string) => string;
    language: string;
}

// Funzione formatMonthlyExpenses
const formatMonthlyExpenses = (expenses: string | number, locale: string = 'it-IT'): string => {

    const formatConfig: Intl.NumberFormatOptions = { 
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    };

    if (typeof expenses === 'number') {
        return new Intl.NumberFormat(locale, formatConfig).format(expenses);
    }
    if (typeof expenses === 'string') {
        if (/^\d+$/.test(expenses)) {
            return new Intl.NumberFormat(locale, formatConfig).format(parseInt(expenses, 10));
        }
        if (/^\d+,\d{2}$/.test(expenses)) {
            const parsed = parseFloat(expenses.replace(',', '.'));
            return new Intl.NumberFormat(locale, formatConfig).format(parsed);
        }
        if (/^\d+\.\d{2}$/.test(expenses)) {
            return new Intl.NumberFormat(locale, formatConfig).format(parseFloat(expenses));
        }
    }
    return String(expenses);
};


const PropertySoldDetails: React.FC<PropertyDetailsProps> = ({
    propertyDetails,
    isResidential,
    isGarage,
    isPlot,
    isValidValue,
    getNestedValue,
    language
}) => {

    const { t, i18n } = useTranslation();
    const currentLocaleForFormatting = i18n.language;
    const yesNo = (value: boolean | undefined | null) => value ? t('common.yes') : t('common.no');

    return (
        <ul className="property-list list-unstyled d-flex flex-wrap">

            {/* GENERAL PROPERTY FIELDS */}
            {propertyFieldsConfig.price && isValidValue(propertyDetails.price) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('common.price')}:</b> €{' '}
                    {new Intl.NumberFormat(currentLocaleForFormatting).format(propertyDetails.price)}
                    {propertyDetails.negotiable ? ` ${t('common.negotiable')}` : ''}
                </li>
            )}

            {propertyFieldsConfig.rif && isValidValue(propertyDetails.ref) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.reference')}:</b> {propertyDetails.ref}
                </li>
            )}

            {propertyFieldsConfig["contractDetails.contractName"] && isValidValue(getNestedValue(propertyDetails, "contractDetails.contractId")) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.contract')}:</b>
                    {getContractTranslation(getNestedValue(propertyDetails, "contractDetails.contractId"), language as LanguageCode)}
                </li>
            )}

            {propertyFieldsConfig["propertyStatusDetails.propertyStatus"] && isValidValue(getNestedValue(propertyDetails, "propertyStatusDetails.propertyStatusId")) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.status')}: </b>
                    {getPropertyStatusTranslation(getNestedValue(propertyDetails, "propertyStatusDetails.propertyStatusId"), language as LanguageCode)}
                </li>
            )}

            {propertyFieldsConfig.sqmt && isValidValue(propertyDetails.sqmt) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.area')}:</b> {propertyDetails.sqmt} m²
                </li>
            )}

            {propertyFieldsConfig.sqmtCommercial && isValidValue(propertyDetails.sqmtCommercial) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.commercialArea')}:</b> {propertyDetails.sqmtCommercial} m²
                </li>
            )}

            {/* --- RESIDENTIAL FIELDS --- */}
            {isResidential && propertyFieldsConfig.premises && isValidValue(propertyDetails.premises) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.roomsCount')}:</b> {propertyDetails.premises}
                </li>
            )}

            {isResidential && propertyFieldsConfig["floorDetails.floor"] && isValidValue(getNestedValue(propertyDetails, "floorDetails.floorId")) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.floor')}:</b>
                    {getFloorTranslation(getNestedValue(propertyDetails, "floorDetails.floorId"), language as LanguageCode)}
                </li>
            )}

            {isResidential && propertyFieldsConfig.floors && isValidValue(propertyDetails.floors) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.totalFloors')}:</b> {propertyDetails.floors}
                </li>
            )}

            {isResidential && propertyFieldsConfig.residential.independentEntry && isValidValue(propertyDetails.residential?.entryDetails.indipendentEntry) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.entry.independent')}:</b> {yesNo(propertyDetails.residential?.entryDetails.indipendentEntry)}
                </li>
            )}

            {isResidential && propertyFieldsConfig.residential.traditionalEntry && isValidValue(propertyDetails.residential?.entryDetails.traditionalEntry) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.entry.traditional')}:</b> {yesNo(propertyDetails.residential?.entryDetails.traditionalEntry)}
                </li>
            )}

            {isResidential && propertyFieldsConfig.residential.lastFloor && isValidValue(propertyDetails.residential?.lastFloor) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.lastFloor')}:</b> {yesNo(propertyDetails.residential?.lastFloor)}
                </li>
            )}

            {/* Terrazzi (logica complessa, traduzione solo Sì/No, il resto sono dati) */}
            {isResidential && propertyFieldsConfig.residential.terraceDetails.terrace && isValidValue(propertyDetails.residential?.terraceDetails.terrace) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.terraces')}:</b>
                    {propertyDetails.residential?.terraceDetails.terrace > 0
                        ? `${yesNo(true)}`
                        : yesNo(false)
                    }
                </li>
            )}

            {/* Camere */}
            {isResidential && !isGarage && propertyFieldsConfig.residential.rooms && isValidValue(propertyDetails.residential?.rooms) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.bedrooms.total')}:</b> {propertyDetails.residential?.rooms}
                </li>
            )}
            {isResidential && propertyFieldsConfig.residential.kingSizeBedRooms && isValidValue(propertyDetails.residential?.kingSizeBedRooms) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.bedrooms.kingSize')}:</b> {propertyDetails.residential?.kingSizeBedRooms}
                </li>
            )}
            {isResidential && propertyFieldsConfig.residential.doubleBedRooms && isValidValue(propertyDetails.residential?.doubleBedRooms) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.bedrooms.double')}:</b> {propertyDetails.residential?.doubleBedRooms}
                </li>
            )}
            {isResidential && propertyFieldsConfig.residential.singleBedRooms && isValidValue(propertyDetails.residential?.singleBedRooms) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.bedrooms.single')}:</b> {propertyDetails.residential?.singleBedRooms}
                </li>
            )}

            {isResidential && propertyFieldsConfig.residential.bathrooms && isValidValue(propertyDetails.residential?.bathrooms) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.bathroomsCount')}:</b> {propertyDetails.residential?.bathrooms}
                </li>
            )}

            {isResidential && propertyFieldsConfig.residential.furnitureDetails.furnitureName && isValidValue(getNestedValue(propertyDetails, "residential.furnitureDetails.furnitureId")) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.furnished')}:</b>
                    {getFurnitureTranslation(getNestedValue(propertyDetails, "residential.furnitureDetails.furnitureId"), language as LanguageCode)}
                </li>
            )}

            {isResidential && !isGarage && propertyFieldsConfig.residential["kitchenDetails.kitchenName"] && isValidValue(propertyDetails.residential?.kitchenDetails.kitchenName) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.kitchen')}:</b> {propertyDetails.residential?.kitchenDetails.kitchenName}
                </li>
            )}

            {/* Soggiorno, Sala, Sala da Pranzo, Ripostiglio, Studio, Mansarda, Taverna (con logica Sì/No e m²) */}
            {(['livingRoom', 'hall', 'diningRoom'] as const).map(roomType => {
                const detailsPath = `residential.${roomType}Details.${roomType}`;
                const sqmtPath = `residential.${roomType}Details.${roomType}Sqmt`;
                const roomKey = `property.${roomType}`;
                // type ResidentialConfig = typeof propertyFieldsConfig.residential;
                let isFieldEnabled = false;
                switch (roomType) {
                    case 'livingRoom':
                        isFieldEnabled = !!(propertyFieldsConfig.residential.livingRoomDetails?.livingRoom);
                        break;
                    case 'hall':
                        isFieldEnabled = !!(propertyFieldsConfig.residential.hallDetails?.hall);
                        break;
                    case 'diningRoom':
                        isFieldEnabled = !!(propertyFieldsConfig.residential.diningRoomDetails?.diningRoom);
                        break;
                }
                return isResidential && isFieldEnabled && isValidValue(getNestedValue(propertyDetails, detailsPath)) && (
                    <li className="col-sm-6 mb-2" key={roomType}>
                        <b>{t(roomKey)}:</b> {yesNo(getNestedValue(propertyDetails, detailsPath))}
                        {getNestedValue(propertyDetails, detailsPath) && getNestedValue(propertyDetails, sqmtPath) > 0
                            ? ` (${getNestedValue(propertyDetails, sqmtPath)} m²)`
                            : ''}
                    </li>
                );
            })}

            {['utilityRoom', 'studio'].map(roomType => {
                let detailsPath = `residential.${roomType}`;
                let sqmtPath = `residential.${roomType}Sqmt`;
                if (roomType === 'studio') {
                   detailsPath = `residential.studioDetails.studio`;
                   sqmtPath = `residential.studioDetails.studioSqmt`;
                }
                const roomKey = `property.${roomType}`;
                const configFieldKey = roomType === 'studio' ? 'studioDetails.studio' : roomType;
                const isFieldEnabled = propertyFieldsConfig.residential[configFieldKey as keyof typeof propertyFieldsConfig.residential];
                return isResidential &&
                    isFieldEnabled &&
                    isValidValue(getNestedValue(propertyDetails, detailsPath)) && (
                    <li className="col-sm-6 mb-2" key={roomType}>
                        <b>{t(roomKey)}:</b> {yesNo(getNestedValue(propertyDetails, detailsPath))}
                        {getNestedValue(propertyDetails, detailsPath) && getNestedValue(propertyDetails, sqmtPath) > 0
                           ? `, ${getNestedValue(propertyDetails, sqmtPath)} m²`
                           : ''}
                    </li>
                );
            })}
             {isResidential && propertyFieldsConfig.residential.mansardDetails.mansard && isValidValue(propertyDetails.residential?.mansardDetails.mansard) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.attic')}:</b> {yesNo(propertyDetails.residential.mansardDetails.mansard)}
                    {propertyDetails.residential.mansardDetails.mansard && propertyDetails.residential.mansardDetails.mansardSqmt > 0
                        ? `, ${propertyDetails.residential.mansardDetails.mansardSqmt} m²`
                        : ''}
                </li>
            )}
             {isResidential && propertyFieldsConfig.residential.tavernDetails.tavern && isValidValue(propertyDetails.residential?.tavernDetails.tavern) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.tavern')}:</b> {yesNo(propertyDetails.residential.tavernDetails.tavern)}
                    {propertyDetails.residential.tavernDetails.tavern && propertyDetails.residential.tavernDetails.tavernSqmt > 0
                        ? ` (${propertyDetails.residential.tavernDetails.tavernSqmt} m²)`
                        : ''}
                </li>
            )}


            {/* Videocitofono, Allarme, Climatizzatore, Addolcitore, Autoclave, Camino, Zanzariere, Porta Blindata, Animali, Studenti */}
            {['videophone', 'alarm', 'waterSoftener', 'autoclave', 'fireplace', 'mosquitoNet', 'strongDoor', 'animalsAllowed', 'studentsAllowed'].map(field => {
                 const translationKeyMap: { [key: string]: string } = {
                    strongDoor: 'securityDoor',
                    studentsAllowed: 'suitableForStudents',
                    mosquitoNet: 'mosquitoNets',
                    // QUI INSERIRE ALTRE MAPPATURE
                 };
                 const detailKey = translationKeyMap[field] || field;
                 const propertyPath = `residential.${field}`;
                 const configPath = `residential.${field}`;

                 return isResidential &&
                     propertyFieldsConfig.residential[configPath as keyof typeof propertyFieldsConfig.residential] &&
                     isValidValue(getNestedValue(propertyDetails, propertyPath)) && (
                     <li className="col-sm-6 mb-2" key={field}>
                         <b>{t(`property.${detailKey}`)}:</b> {yesNo(getNestedValue(propertyDetails, propertyPath))}
                     </li>
                 );
            })}
            {isResidential && propertyFieldsConfig.residential.conditionerDetails.conditioner && isValidValue(propertyDetails.residential?.conditionerDetails.conditioner) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.airConditioning')}:</b> {yesNo(propertyDetails.residential.conditionerDetails.conditioner)}
                </li>
            )}


            {/* Riscaldamento */}
            {isResidential && propertyFieldsConfig.residential.heatingDetails.heatingName && isValidValue(getNestedValue(propertyDetails, "residential.heatingDetails.heatingId")) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.heating.label')}:</b> {getHeatingTypeTranslation(getNestedValue(propertyDetails, "residential.heatingDetails.heatingId"), language as LanguageCode)}
                </li>
            )}
            {isResidential && propertyFieldsConfig.residential.heatingDetails.heatingTypeName && isValidValue(getNestedValue(propertyDetails, "residential.heatingDetails.heatingTypeId")) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.heating.type')}:</b> {getHeatingTypeTranslation(getNestedValue(propertyDetails, "residential.heatingDetails.heatingTypeId"), language as LanguageCode)}
                </li>
            )}
            {isResidential && propertyFieldsConfig.residential.heatingDetails.heatingFuelName && isValidValue(getNestedValue(propertyDetails, "residential.heatingDetails.heatingFuelId")) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.heating.fuel')}:</b> {getHeatingFuelNameTranslation(getNestedValue(propertyDetails, "residential.heatingDetails.heatingFuelId"), language as LanguageCode)}
                </li>
            )}


            {/* Posti Auto */}
            {isResidential && propertyFieldsConfig.residential.carPlaces && propertyDetails.residential?.carPlaces !== '0' && isValidValue(propertyDetails.residential?.carPlaces) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.parking.spaces')}:</b> {propertyDetails.residential?.carPlaces}
                </li>
            )}
            {isResidential && propertyFieldsConfig.residential.carBoxDetails.carBoxName && propertyDetails.residential?.carPlaces !== '0' && isValidValue(getNestedValue(propertyDetails, "residential.carBoxDetails.carBoxName")) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.parking.type')}:</b> {getNestedValue(propertyDetails, "residential.carBoxDetails.carBoxName")}
                </li>
            )}
            {isResidential && propertyFieldsConfig.residential.unclampedCarPlaces && isValidValue(propertyDetails.residential?.unclampedCarPlaces) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.parking.outdoorSpaces')}:</b> {propertyDetails.residential.unclampedCarPlaces}
                </li>
            )}

            {/* Garage */}
            {isResidential && propertyFieldsConfig.residential.garageDetails.garage1 && isValidValue(propertyDetails.residential?.garageDetails.garage1) && propertyFieldsConfig.residential.garageDetails.garage2 && isValidValue(propertyDetails.residential?.garageDetails.garage2) ? (
                <>
                    <li className="col-sm-6 mb-2">
                        <b>{t('property.parking.garage1')}:</b> {yesNo(propertyDetails.residential?.garageDetails.garage1)} {propertyDetails.residential?.garageDetails.garage1 && propertyDetails.residential?.garageDetails.garage1Sqmt > 0 ? `(${propertyDetails.residential?.garageDetails.garage1Sqmt} m²)` : ''}
                    </li>
                    <li className="col-sm-6 mb-2">
                        <b>{t('property.parking.garage2')}:</b> {yesNo(propertyDetails.residential?.garageDetails.garage2)} {propertyDetails.residential?.garageDetails.garage2 && propertyDetails.residential?.garageDetails.garage2Sqmt > 0 ? `(${propertyDetails.residential?.garageDetails.garage2Sqmt} m²)` : ''}
                    </li>
                </>
            ) : isResidential && propertyFieldsConfig.residential.garageDetails.garage1 && isValidValue(propertyDetails.residential?.garageDetails.garage1) ? (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.parking.garage')}:</b> {yesNo(propertyDetails.residential?.garageDetails.garage1)} {propertyDetails.residential?.garageDetails.garage1 && propertyDetails.residential?.garageDetails.garage1Sqmt > 0 ? `(${propertyDetails.residential?.garageDetails.garage1Sqmt} m²)` : ''}
                </li>
            ) : isResidential && propertyFieldsConfig.residential.garageDetails.garage2 && isValidValue(propertyDetails.residential?.garageDetails.garage2) ? (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.parking.garage')}:</b> {yesNo(propertyDetails.residential?.garageDetails.garage2)} {propertyDetails.residential?.garageDetails.garage2 && propertyDetails.residential?.garageDetails.garage2Sqmt > 0 ? `(${propertyDetails.residential?.garageDetails.garage2Sqmt} m²)` : ''}
                </li>
            ) : null}


            {/* Giardino, Giardino Privato, Giardino Condominiale, Resede, Terreno, Altro Terreno */}
            {['garden', 'privateGarden', 'condominiumGarden', 'patio', 'land', 'otherLand'].map(field => {
                const detailsPath = `residential.gardenDetails.${field}`;
                const sqmtPath = `residential.gardenDetails.${field}Sqmt`;
                let labelKey = `property.garden.${field}`;

                if (field === 'patio') {
                    labelKey = 'property.courtyard';
                } else if (field === 'land') {
                    labelKey = 'property.land';
                } else if (field === 'otherLand') {
                    labelKey = 'property.otherLand';
                }

                return isResidential &&
                    propertyFieldsConfig.residential.gardenDetails?.[field as keyof typeof propertyFieldsConfig.residential.gardenDetails] &&
                    isValidValue(getNestedValue(propertyDetails, detailsPath)) && (
                    <li className="col-sm-6 mb-2" key={field}>
                        <b>{t(labelKey)}:</b>
                        {field === 'otherLand'
                            ? getNestedValue(propertyDetails, detailsPath)
                            : yesNo(getNestedValue(propertyDetails, detailsPath))
                        }
                        {field !== 'otherLand' && getNestedValue(propertyDetails, detailsPath) && getNestedValue(propertyDetails, sqmtPath) > 0
                            ? (field === 'privateGarden' || field === 'condominiumGarden' ? ` (${getNestedValue(propertyDetails, sqmtPath)} m²)` : `, ${getNestedValue(propertyDetails, sqmtPath)} m²`)
                            : ''}
                    </li>
                );
            })}

            {/* Spese Mensili */}
            {propertyFieldsConfig.monthlyExpenses && isValidValue(propertyDetails.monthlyExpenses) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.monthlyExpenses')}:</b> {formatMonthlyExpenses(propertyDetails.monthlyExpenses, currentLocaleForFormatting)}
                </li>
            )}

            {/* Classe Energetica e IPE */}
            {propertyFieldsConfig.enerClassDetails.name && isValidValue(propertyDetails.enerClassDetails?.enerClassName) && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.energyClass.label')}:</b> {t(`property.energyClass.status.${propertyDetails.enerClassDetails.enerClassName}`, {defaultValue: propertyDetails.enerClassDetails.enerClassName})}
                </li>
            )}
            {propertyDetails.apeDetails?.apeAfterOct2015 ? (
                <>
                    {propertyFieldsConfig.apeDetails.apeAfterOct2015 && isValidValue(propertyDetails.ipe) && ( // IPE è EPgl,nren
                        <li className="col-sm-6 mb-2">
                            <b>{t('property.energyClass.epGlNren')}:</b> {Number(propertyDetails.ipe).toFixed(2)} {t('property.energyClass.unit')}
                        </li>
                    )}
                    {propertyFieldsConfig.apeDetails.apeRin && isValidValue(propertyDetails.apeDetails?.apeRin) && (
                        <li className="col-sm-6 mb-2">
                            <b>{t('property.energyClass.epGlRen')}:</b> {Number(propertyDetails.apeDetails.apeRin).toFixed(2)} {t('property.energyClass.unit')}
                        </li>
                    )}
                </>
            ) : (
                propertyFieldsConfig.ipe && isValidValue(propertyDetails.ipe) && (
                    <li className="col-sm-6 mb-2">
                        <b>{t('property.energyClass.IPE')}:</b> {Number(propertyDetails.ipe).toFixed(2)} {t('property.energyClass.unit')}
                    </li>
                )
            )}

            {/* Prestazioni Fabbricato */}
            {propertyFieldsConfig.performanceDetails.winter && isValidValue(propertyDetails.performanceDetails?.winter) && propertyDetails.performanceDetails?.winter !== "0" && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.performance.winter')}:</b> {t(`property.energyClass.levels.${propertyDetails.performanceDetails.winter.toLowerCase()}`, {defaultValue: propertyDetails.performanceDetails.winter})}
                </li>
            )}
            {propertyFieldsConfig.performanceDetails.summer && isValidValue(propertyDetails.performanceDetails?.summer) && propertyDetails.performanceDetails?.summer !== "0" && (
                <li className="col-sm-6 mb-2">
                    <b>{t('property.performance.summer')}:</b> {t(`property.energyClass.levels.${propertyDetails.performanceDetails.summer.toLowerCase()}`, {defaultValue: propertyDetails.performanceDetails.summer})}
                </li>
            )}


            {/* --- PLOT FIELDS (Terreni) --- */}
            {isPlot && Object.entries(propertyFieldsConfig.serviceDetails).map(([key, isEnabled]) => {
                const utilityKey = `property.utilities.${key.replace('Available','').replace('Availability','').replace('System','').replace('Access','').replace('Line','').replace('Lightning','').toLowerCase()}`;
                const valuePath = `serviceDetails.${key}`;
                const finalUtilityKey = key === 'publicLightning' ? 'property.utilities.publicLighting' : utilityKey;

                return isEnabled && isValidValue(getNestedValue(propertyDetails, valuePath)) && (
                    <li className="col-sm-6 mb-2" key={key}>
                        <b>{t(finalUtilityKey)}:</b> {yesNo(getNestedValue(propertyDetails, valuePath))}
                    </li>
                );
            })}
        </ul>
    );
};

export default PropertySoldDetails;